import { useRef, useState, type ReactNode } from 'react';
import {
  ChevronDown,
  ChevronUp,
  Clapperboard,
  ExternalLink,
  Eye,
  EyeOff,
  Image as ImageIcon,
  Monitor,
  Pencil,
  Plus,
  Smartphone,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { Button, cn } from '@jeraisonne/ui';
import {
  HERO_DURATION_DEFAULT_MS,
  HERO_MAX_BULLETS,
  HERO_MEDIA_POSITIONS,
  HERO_MEDIA_POSITION_LABELS,
  guessHeroMediaType,
  heroDurationFromSeconds,
  nextHeroDisplayOrder,
  type HeroDevice,
  type HeroMediaPosition,
  type HeroMediaType,
  type HeroPhase,
  type HeroPhaseInput,
} from '@jeraisonne/db/landing-hero';
import { useAuth, isAdmin } from '@/lib/auth';
import {
  heroInputError,
  uploadHeroMedia,
  useAdminHeroPhases,
  useCreateHeroPhase,
  useDeleteHeroPhase,
  useMoveHeroPhase,
  useSeedDefaultHeroPhases,
  useUpdateHeroPhase,
} from '@/lib/admin/landing-hero';

const SITE_URL = 'https://www.jeraisonne.fr';

const inputCls =
  'border-ink-200 text-ink-900 placeholder:text-ink-400 focus:ring-primary-400 w-full rounded-xl border px-3 py-2.5 font-sans text-[14px] focus:outline-none focus:ring-2';
const labelCls = 'font-display text-ink-700 text-[12px] font-semibold';

/** Formulaire → valeurs éditables (texte des puces, durée en secondes). */
type FormState = {
  eyebrow: string;
  title: string;
  title_highlight: string;
  description: string;
  bullets: string[];
  caption_title: string;
  caption_subtitle: string;
  media_type: HeroMediaType;
  media_url: string;
  poster_url: string;
  media_position: HeroMediaPosition;
  device: HeroDevice;
  seconds: number;
  is_published: boolean;
  display_order: number;
};

function toForm(phase: HeroPhase | null, order: number): FormState {
  return {
    eyebrow: phase?.eyebrow ?? '',
    title: phase?.title ?? '',
    title_highlight: phase?.title_highlight ?? '',
    description: phase?.description ?? '',
    bullets: phase?.bullets ?? [],
    caption_title: phase?.caption_title ?? '',
    caption_subtitle: phase?.caption_subtitle ?? '',
    media_type: phase?.media_type ?? 'image',
    media_url: phase?.media_url ?? '',
    poster_url: phase?.poster_url ?? '',
    media_position: phase?.media_position ?? 'center',
    device: phase?.device ?? 'mobile',
    seconds: (phase?.duration_ms ?? HERO_DURATION_DEFAULT_MS) / 1000,
    is_published: phase?.is_published ?? true,
    display_order: phase?.display_order ?? order,
  };
}

export function toHeroInput(f: FormState): HeroPhaseInput {
  return {
    display_order: f.display_order,
    is_published: f.is_published,
    eyebrow: f.eyebrow,
    title: f.title,
    title_highlight: f.title_highlight,
    description: f.description,
    bullets: f.bullets.map((b) => b.trim()).filter(Boolean),
    caption_title: f.caption_title,
    caption_subtitle: f.caption_subtitle,
    media_type: f.media_type,
    media_url: f.media_url,
    poster_url: f.poster_url,
    media_position: f.media_position,
    device: f.device,
    duration_ms: heroDurationFromSeconds(f.seconds),
  };
}

/** Aperçu simplifié : fond + voile + texte (sans le player). */
function PhasePreview({ f }: { f: FormState }) {
  const src = f.media_type === 'video' ? f.poster_url || null : f.media_url || null;
  return (
    <div className="relative aspect-[16/9] w-full overflow-hidden rounded-2xl bg-[#061433]">
      {f.media_type === 'video' && f.media_url ? (
        <video
          src={f.media_url}
          poster={f.poster_url || undefined}
          muted
          loop
          autoPlay
          playsInline
          className="absolute inset-0 h-full w-full object-cover"
          style={{ objectPosition: f.media_position }}
        />
      ) : src ? (
        <img
          src={src}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
          style={{ objectPosition: f.media_position }}
        />
      ) : null}
      <div className="absolute inset-0 bg-gradient-to-r from-[#061433]/90 via-[#061433]/55 to-[#061433]/5" />
      <div className="relative flex h-full flex-col justify-center gap-1.5 p-4 text-white sm:p-6">
        <p className="text-[10px] font-extrabold tracking-[0.18em] text-[#f7d068]">{f.eyebrow}</p>
        <p className="max-w-[60%] font-serif text-lg font-semibold leading-tight sm:text-2xl">
          {f.title}
          {f.title_highlight ? (
            <span className="text-primary-300 block">{f.title_highlight}</span>
          ) : null}
        </p>
        <p className="line-clamp-2 max-w-[60%] text-[11px] text-white/80 sm:text-xs">
          {f.description}
        </p>
      </div>
      <span className="absolute bottom-2 right-2 flex items-center gap-1 rounded-full bg-white/20 px-2 py-1 text-[11px] font-semibold text-white">
        {f.device === 'desktop' ? (
          <Monitor className="h-3.5 w-3.5" />
        ) : (
          <Smartphone className="h-3.5 w-3.5" />
        )}
        {f.device === 'desktop' ? 'Ordinateur' : 'Téléphone'}
      </span>
    </div>
  );
}

function MediaField({
  label,
  value,
  onChange,
  accept,
  onUploaded,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  accept: string;
  onUploaded?: (url: string, file: File) => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const url = await uploadHeroMedia(file);
      onChange(url);
      onUploaded?.(url, file);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Dépôt impossible.');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <span className={labelCls}>{label}</span>
      <div className="flex flex-col gap-2 sm:flex-row">
        <input
          className={inputCls}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://…"
          aria-label={label}
        />
        <Button
          type="button"
          variant="outline"
          onClick={() => fileRef.current?.click()}
          loading={busy}
          className="min-h-[2.75rem] shrink-0"
        >
          <Upload className="mr-1.5 h-4 w-4" /> Déposer
        </Button>
        <input
          ref={fileRef}
          type="file"
          accept={accept}
          className="hidden"
          onChange={(e) => void onFile(e.target.files?.[0])}
        />
      </div>
      {error ? <p className="text-accent-600 text-xs">{error}</p> : null}
    </div>
  );
}

function PhaseForm({
  phase,
  order,
  onClose,
}: {
  phase: HeroPhase | null;
  order: number;
  onClose: () => void;
}) {
  const [f, setF] = useState<FormState>(() => toForm(phase, order));
  const [error, setError] = useState<string | null>(null);
  const create = useCreateHeroPhase();
  const update = useUpdateHeroPhase();
  const pending = create.isPending || update.isPending;
  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setF((s) => ({ ...s, [k]: v }));

  const save = async () => {
    const input = toHeroInput(f);
    const issue = heroInputError(input);
    if (issue) {
      setError(issue);
      return;
    }
    setError(null);
    try {
      if (phase) await update.mutateAsync({ id: phase.id, input });
      else await create.mutateAsync(input);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Enregistrement impossible.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center sm:p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={phase ? 'Modifier la phase' : 'Nouvelle phase'}
        className="ocean:bg-[#061433] flex max-h-dvh w-full flex-col bg-white sm:max-h-[92dvh] sm:max-w-2xl sm:rounded-2xl"
      >
        <header className="border-ink-100 flex items-center gap-3 border-b px-4 py-3">
          <h2 className="font-display text-ink-900 flex-1 text-lg font-bold">
            {phase ? 'Modifier la phase' : 'Nouvelle phase'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            className="text-ink-500 flex h-11 w-11 items-center justify-center rounded-full active:scale-95"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
          <PhasePreview f={f} />

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="flex flex-col gap-1">
              <span className={labelCls}>Légende de la scène</span>
              <input
                className={inputCls}
                value={f.caption_title}
                maxLength={60}
                onChange={(e) => set('caption_title', e.target.value)}
                placeholder="Dans sa chambre"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className={labelCls}>Sous-légende (facultatif)</span>
              <input
                className={inputCls}
                value={f.caption_subtitle}
                maxLength={100}
                onChange={(e) => set('caption_subtitle', e.target.value)}
                placeholder="Une dernière révision avant de dormir"
              />
            </label>
          </div>

          <label className="flex flex-col gap-1">
            <span className={labelCls}>Surtitre</span>
            <input
              className={inputCls}
              value={f.eyebrow}
              maxLength={60}
              onChange={(e) => set('eyebrow', e.target.value)}
              placeholder="LA RÉVISION QUI TE SUIT"
            />
          </label>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="flex flex-col gap-1">
              <span className={labelCls}>Titre</span>
              <input
                className={inputCls}
                value={f.title}
                maxLength={120}
                onChange={(e) => set('title', e.target.value)}
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className={labelCls}>Titre en couleur (2e ligne, facultatif)</span>
              <input
                className={inputCls}
                value={f.title_highlight}
                maxLength={120}
                onChange={(e) => set('title_highlight', e.target.value)}
              />
            </label>
          </div>
          <label className="flex flex-col gap-1">
            <span className={labelCls}>Description ({f.description.length} / 400)</span>
            <textarea
              className={`${inputCls} min-h-[6rem] resize-y`}
              value={f.description}
              maxLength={400}
              onChange={(e) => set('description', e.target.value)}
            />
          </label>

          <div className="flex flex-col gap-2">
            <span className={labelCls}>Puces (jusqu’à {HERO_MAX_BULLETS})</span>
            {f.bullets.map((b, i) => (
              <div key={i} className="flex gap-2">
                <input
                  className={inputCls}
                  value={b}
                  maxLength={80}
                  aria-label={`Puce ${i + 1}`}
                  onChange={(e) =>
                    set(
                      'bullets',
                      f.bullets.map((x, j) => (j === i ? e.target.value : x)),
                    )
                  }
                />
                <button
                  type="button"
                  aria-label={`Retirer la puce ${i + 1}`}
                  onClick={() =>
                    set(
                      'bullets',
                      f.bullets.filter((_, j) => j !== i),
                    )
                  }
                  className="text-ink-500 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl active:scale-95"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
            {f.bullets.length < HERO_MAX_BULLETS ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="self-start"
                onClick={() => set('bullets', [...f.bullets, ''])}
              >
                <Plus className="mr-1 h-4 w-4" /> Ajouter une puce
              </Button>
            ) : null}
          </div>

          <fieldset className="flex flex-col gap-2">
            <legend className={labelCls}>Fond</legend>
            <div className="flex gap-2">
              {(['image', 'video'] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  aria-pressed={f.media_type === t}
                  onClick={() => set('media_type', t)}
                  className={cn(
                    'min-h-[2.75rem] flex-1 rounded-xl border px-3 text-sm font-semibold active:scale-[0.98]',
                    f.media_type === t
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-ink-200 text-ink-600',
                  )}
                >
                  {t === 'image' ? 'Photo' : 'Vidéo'}
                </button>
              ))}
            </div>
            <MediaField
              label={
                f.media_type === 'video'
                  ? 'Vidéo (MP4 / WebM, 30 Mo max)'
                  : 'Photo (JPEG / PNG / WebP, 5 Mo max)'
              }
              value={f.media_url}
              onChange={(v) => set('media_url', v)}
              accept={
                f.media_type === 'video'
                  ? 'video/mp4,video/webm'
                  : 'image/jpeg,image/png,image/webp'
              }
              onUploaded={(url) => set('media_type', guessHeroMediaType(url))}
            />
            {f.media_type === 'video' ? (
              <MediaField
                label="Image d’attente de la vidéo (facultatif)"
                value={f.poster_url}
                onChange={(v) => set('poster_url', v)}
                accept="image/jpeg,image/png,image/webp"
              />
            ) : null}
            <label className="flex flex-col gap-1">
              <span className={labelCls}>Cadrage</span>
              <select
                className={inputCls}
                value={f.media_position}
                onChange={(e) => set('media_position', e.target.value as HeroMediaPosition)}
              >
                {HERO_MEDIA_POSITIONS.map((p) => (
                  <option key={p} value={p}>
                    {HERO_MEDIA_POSITION_LABELS[p]}
                  </option>
                ))}
              </select>
            </label>
          </fieldset>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <fieldset className="flex flex-col gap-1">
              <legend className={labelCls}>Le player s’affiche sur</legend>
              <div className="mt-1 flex gap-2">
                {(['mobile', 'desktop'] as const).map((d) => (
                  <button
                    key={d}
                    type="button"
                    aria-pressed={f.device === d}
                    onClick={() => set('device', d)}
                    className={cn(
                      'flex min-h-[2.75rem] flex-1 items-center justify-center gap-1.5 rounded-xl border px-3 text-sm font-semibold active:scale-[0.98]',
                      f.device === d
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-ink-200 text-ink-600',
                    )}
                  >
                    {d === 'mobile' ? (
                      <Smartphone className="h-4 w-4" />
                    ) : (
                      <Monitor className="h-4 w-4" />
                    )}
                    {d === 'mobile' ? 'Téléphone' : 'Ordinateur'}
                  </button>
                ))}
              </div>
            </fieldset>
            <label className="flex flex-col gap-1">
              <span className={labelCls}>Durée (secondes, 3 à 30)</span>
              <input
                type="number"
                min={3}
                max={30}
                step={0.5}
                className={inputCls}
                value={f.seconds}
                onChange={(e) => set('seconds', Number(e.target.value))}
              />
            </label>
          </div>

          <label className="flex min-h-[2.75rem] items-center gap-3">
            <input
              type="checkbox"
              checked={f.is_published}
              onChange={(e) => set('is_published', e.target.checked)}
              className="h-5 w-5"
            />
            <span className="text-ink-800 text-sm font-semibold">Affichée sur le site</span>
          </label>

          {error ? <p className="text-accent-600 text-sm font-semibold">{error}</p> : null}
        </div>

        <footer className="border-ink-100 flex flex-col-reverse gap-2 border-t px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" onClick={onClose} fullWidth className="sm:w-auto">
            Annuler
          </Button>
          <Button
            type="button"
            onClick={() => void save()}
            loading={pending}
            fullWidth
            className="sm:w-auto"
          >
            Enregistrer
          </Button>
        </footer>
      </div>
    </div>
  );
}

export function TutorAdminHeroPage() {
  const { profile, user } = useAuth();
  const canEdit = isAdmin(profile, user?.email);
  const { data: phases = [], isLoading, error } = useAdminHeroPhases();
  const move = useMoveHeroPhase();
  const remove = useDeleteHeroPhase();
  const update = useUpdateHeroPhase();
  const seed = useSeedDefaultHeroPhases();
  const [editing, setEditing] = useState<HeroPhase | 'new' | null>(null);

  if (!canEdit) {
    return (
      <div className="mx-auto w-full max-w-3xl px-4 py-6">
        <p className="text-ink-500 text-sm">Réservé aux admins.</p>
      </div>
    );
  }

  const totalSeconds =
    phases.filter((p) => p.is_published).reduce((s, p) => s + p.duration_ms, 0) / 1000;

  return (
    <div className="mx-auto w-full max-w-4xl px-4 py-6 sm:py-8">
      <header className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start">
        <div className="flex-1">
          <h1 className="font-display text-ink-900 flex items-center gap-2 text-2xl font-bold">
            <Clapperboard className="text-primary-500 h-6 w-6" /> Hero de la landing
          </h1>
          <p className="text-ink-500 mt-1 text-sm">
            Les phases s’enchaînent dans l’ordre en haut de la page d’accueil : un fond, un
            argumentaire et le player d’exercice sur téléphone ou ordinateur. Le site se met à jour
            dans l’heure. L’exercice joué vient de la « Playlist showcase ».
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:items-end">
          <Button type="button" onClick={() => setEditing('new')} fullWidth className="sm:w-auto">
            <Plus className="mr-1.5 h-4 w-4" /> Ajouter une phase
          </Button>
          <a
            href={SITE_URL}
            target="_blank"
            rel="noreferrer"
            className="text-primary-600 inline-flex min-h-[2.75rem] items-center gap-1 text-sm font-semibold"
          >
            Voir sur le site <ExternalLink className="h-4 w-4" />
          </a>
        </div>
      </header>

      {error ? <p className="text-accent-600 text-sm">{(error as Error).message}</p> : null}
      {isLoading ? <p className="text-ink-500 text-sm">Chargement…</p> : null}

      {!isLoading && phases.length === 0 ? (
        <div className="border-ink-200 rounded-2xl border border-dashed p-6 text-center">
          <p className="text-ink-600 text-sm">
            Aucune phase : le site affiche les 5 phases par défaut.
          </p>
          <Button
            type="button"
            variant="outline"
            className="mt-3"
            loading={seed.isPending}
            onClick={() => seed.mutate()}
          >
            Remettre les 5 phases par défaut
          </Button>
        </div>
      ) : null}

      {phases.length > 0 ? (
        <p className="text-ink-500 mb-3 text-xs">
          {phases.filter((p) => p.is_published).length} phase(s) affichée(s) · un tour complet dure{' '}
          {totalSeconds.toLocaleString('fr-FR')} s
        </p>
      ) : null}

      <ul className="space-y-3">
        {phases.map((p, i) => {
          const thumb = p.media_type === 'video' ? p.poster_url : p.media_url;
          return (
            <li
              key={p.id}
              className={cn(
                'border-ink-100 flex flex-col gap-3 rounded-2xl border bg-white p-3 sm:flex-row sm:items-center',
                !p.is_published && 'opacity-60',
              )}
            >
              <div className="bg-ink-100 relative aspect-[16/9] w-full shrink-0 overflow-hidden rounded-xl sm:w-40">
                {thumb ? (
                  <img src={thumb} alt="" className="h-full w-full object-cover" />
                ) : (
                  <ImageIcon className="text-ink-400 absolute inset-0 m-auto h-6 w-6" />
                )}
                {p.media_type === 'video' ? (
                  <span className="absolute left-1.5 top-1.5 rounded bg-black/60 px-1.5 py-0.5 text-[11px] font-semibold text-white">
                    Vidéo
                  </span>
                ) : null}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-ink-500 text-xs font-semibold">
                  {String(i + 1).padStart(2, '0')} · {p.caption_title}
                </p>
                <p className="text-ink-900 truncate font-semibold">
                  {p.title} {p.title_highlight}
                </p>
                <p className="text-ink-500 mt-0.5 flex items-center gap-2 text-xs">
                  {p.device === 'desktop' ? (
                    <Monitor className="h-3.5 w-3.5" />
                  ) : (
                    <Smartphone className="h-3.5 w-3.5" />
                  )}
                  {p.device === 'desktop' ? 'Ordinateur' : 'Téléphone'} ·{' '}
                  {(p.duration_ms / 1000).toLocaleString('fr-FR')} s
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <IconButton
                  label="Monter"
                  disabled={i === 0 || move.isPending}
                  onClick={() => move.mutate({ phases, id: p.id, delta: -1 })}
                >
                  <ChevronUp className="h-4 w-4" />
                </IconButton>
                <IconButton
                  label="Descendre"
                  disabled={i === phases.length - 1 || move.isPending}
                  onClick={() => move.mutate({ phases, id: p.id, delta: 1 })}
                >
                  <ChevronDown className="h-4 w-4" />
                </IconButton>
                <IconButton
                  label={p.is_published ? 'Masquer du site' : 'Afficher sur le site'}
                  onClick={() => {
                    const { id: _id, ...rest } = p;
                    update.mutate({ id: p.id, input: { ...rest, is_published: !p.is_published } });
                  }}
                >
                  {p.is_published ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                </IconButton>
                <IconButton label="Modifier" onClick={() => setEditing(p)}>
                  <Pencil className="h-4 w-4" />
                </IconButton>
                <IconButton
                  label="Supprimer"
                  onClick={() => {
                    if (window.confirm(`Supprimer la phase « ${p.caption_title} » ?`))
                      remove.mutate(p.id);
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                </IconButton>
              </div>
            </li>
          );
        })}
      </ul>

      {editing ? (
        <PhaseForm
          phase={editing === 'new' ? null : editing}
          order={nextHeroDisplayOrder(phases)}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </div>
  );
}

function IconButton({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      disabled={disabled}
      className="text-ink-600 hover:bg-ink-50 flex h-11 w-11 items-center justify-center rounded-xl transition active:scale-95 disabled:opacity-30"
    >
      {children}
    </button>
  );
}
